import { Component, computed, effect, inject, signal, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { EChartsOption } from 'echarts';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';

import { RegisterComponent } from '../../components/component-lookup';
import { VisualBaseComponent } from '../visual-base.component';

import { GroupByPipe } from '../../../shared/pipes/group-by.pipe';
import { forkJoin } from 'rxjs';
import { GlobalConstants } from '../../../shared/global-constants';

import { DateRange, StateService } from '../../services/state.service';
import { InstitutionStatistical } from '../../services/metric.service';
import { RateService, RateDefinition } from '../../services/rate.service';
import { VisualService } from '../../services/visual.service';
import { SessionService } from '../../../shared/services/session.service';
import { CreateEffectOptions } from '@angular/core';
import { DropDownService, RegressionDropdown, RegressionRate } from '../../services/drop.down.service';
import { FilterRegressionRatePipe } from "../../pipes/filter-regression-rate.pipe";
import { FilterRegressionMetricPipe } from "../../pipes/filter-regression-metric.pipe";
import { ChartInfoBoxComponent } from "../chart-info-box/chart-info-box.component";



@RegisterComponent('RegressionComponent')
@Component({
  selector: 'app-regression',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxEchartsDirective,
    GroupByPipe, FilterRegressionRatePipe, FilterRegressionMetricPipe, ChartInfoBoxComponent],
  templateUrl: './regression.component.html',
  styleUrl: './regression.component.scss',
  providers: [provideEcharts()]
})
export class RegressionComponent extends VisualBaseComponent {
  readonly institution = inject(StateService).institution;
  readonly dateRange = inject(StateService).dateRange;

  private sessionService = inject(SessionService);
  private rateService = inject(RateService);
  private dropDownService = inject(DropDownService);
  readonly regressionFormula = signal<string>('');
  readonly isScatter = signal<boolean>(false);
  regressionType = input<string>('');
  readonly dialogInformationId = input<string>('');

  override initOptions = { height: 400 }
  override baseOptions: EChartsOption = {
    grid: {
      left: 10,
      right: 25,
      top: 20,
      bottom: 40,
      containLabel: true
    },
    legend: {
      selectedMode: true
    },
    xAxis: {
      type: 'value',
      show: true
    },
    yAxis: {
      type: 'value',
      show: true
    },
    series: [
      {
        type: 'scatter'
      },
      {
        type: 'line',
        symbol: 'none'
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };
  override tooltipOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      valueFormatter: value => VisualService.getValueDisplay(Number(value), "DEC")
    }
  };
  override printOptions: EChartsOption = {
    animation: false,
    series: [
      {
        type: 'scatter'
      },
      {
        type: 'line',
        symbol: 'none',
        symbolSize: 7,
        lineStyle: {
          type: [20, 5],
          dashOffset: 5,
        }
      },
      {
        type: 'line',
        symbol: 'none'
      }
    ]
  };

  regressionMetrics = signal<RegressionDropdown[]>([]);
  readonly metric = signal<RegressionDropdown | undefined>(undefined);
  readonly correlationRates = computed(() => this.metric()?.rates ?? []);
  readonly rate = signal<RegressionRate | undefined>(undefined);

  private statisticals?: InstitutionStatistical[];
  readonly statistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly bestStatistical = signal<InstitutionStatistical | undefined>(undefined);
  readonly insufficientData = computed(() => this.statistical()?.beta == null);

  metricListOpen = false;
  rateListOpen = false;
  openMetricCategories: string[] = [];
  openRateCategories: string[] = [];
  metricSearch = '';
  rateSearch = '';
  noData = false;
  onlyOneCategory = signal<boolean>(false);
  rateDefinition: RateDefinition | undefined;
  attributionDate: Date | undefined;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // override resizedObserver = new ResizeObserver((entries) => {
  //   if (!this.echartsInstance?.isDisposed()) {
  //     this.echartsInstance?.resize();
  //   }
  // });

  constructor() {
    super();

    effect(() => {
      const institution = this.institution();
      if (institution?.institutionType) {
        this.dropDownService.getRegressionDropdown().subscribe(metrics => {
          setTimeout(() => {
            this.regressionMetrics.set(metrics);
            this.onlyOneCategory.set(this.hasOnlyOneDistinctValue(metrics, 'categoryTitle'));
            const metricId = this.sessionService
              .get(`regression-metricId-${this.institution()?.institutionType}`)
              ?.toString() ?? this.metric()?.metricId ?? metrics[0]?.metricId;
            this.selectMetric(metricId);
          });
        });
      }
    });

    effect(() => {
      const dateRange = this.dateRange();
      const metric = this.metric();
      const rate = this.rate();
      this.rateService.setSelecteRegressionRate(rate);
      if (this.chartInit() && dateRange && metric && rate) {
        this.rateService.getRateDefinition(rate.rateName.trim()).subscribe((result) => {
          this.rateDefinition = result;
          this.attributionDate = this.subtractQuarters(result.rateEndDate, result.lagValue)
        });
        this.updateVisual(dateRange, metric, rate);
      }
    }, { allowSignalWrites: true } as CreateEffectOptions);
  }

  public updateVisual(dateRange: DateRange, metric: RegressionDropdown, rate: RegressionRate) {
    this.metricService.getInstitutionStatisticals(this.institution().iid, metric.metricId, dateRange, 'non-maturity-deposits').subscribe(statisticals => {
      this.statisticals = statisticals;
      setTimeout(() => {
        this.statistical.set(this.statisticals?.find(s => s.rateId == rate.rateIdStatistical) ?? {} as InstitutionStatistical);
        this.bestStatistical.set(this.statisticals?.reduce((p, c) => ((p.rSquared ?? 0) > (c.rSquared ?? 0)) ? p : c, {} as InstitutionStatistical) ?? {} as InstitutionStatistical);

      });
    });


    forkJoin({
      metricData: this.metricService.getMetricData(this.institution().iid, metric.metricId, undefined, 'StartNmdReportDate'),
      rateData: this.rateService.getRateData(rate.rateIdStatistical, 'StartNmdReportDate')
    }).subscribe(({ metricData, rateData }) => {

      const metricDataSeries = this.visualService.getMetricSeriesPeer(this.metric()?.displayType, metricData, dateRange, 'non-maturity-deposits');
      const rateSeries: any[] = [];
      rateData.forEach((data: { reportDate: Date; value: number; }) => {
        const dataRow = metricDataSeries.dates.findIndex(d => { return new Date(d).getTime() == new Date(data.reportDate).getTime() });
        if (dataRow > -1) {
          rateSeries[dataRow] = Number(data.value).toFixed(2);
        }
      });

      if (metricDataSeries.institutionSeries.filter(v => v != null).length == 0) {
        this.echartsInstance?.clear();
        this.chartOptions = this.noDataOptions;
        this.noData = true;
      }
      else {
        if (this.noData) {
          this.setOptions();
          this.noData = false;
        }

        const sanitizedRateSeries = rateSeries
          .map(value => (typeof value === 'string' ? parseFloat(value) : value))
          .filter(value => !isNaN(value)); // Ensure valid numeric values only

        // When there's null cost data, make all the arrays the same length and remove the index for those wehre there's no data
        const replaceEmptyBankCost = Array.from(metricDataSeries.institutionSeries, (item) => (item === undefined ? undefined : item));
        const cleanedBankCost = replaceEmptyBankCost.filter((value) => value !== undefined && value !== null) as number[];;
        const cleanedRate = sanitizedRateSeries.filter((_, index) => metricDataSeries.institutionSeries[index] !== undefined);

        const linearRegression = this.calculateLinearRegression(cleanedRate, cleanedBankCost);

        this.regressionFormula.set(`y = ${this.formatSlope(linearRegression.slope)}x + ${this.formatSlope(linearRegression.intercept)}`);
        this.isScatter.set(this.regressionType() === 'scatter' ? true : false);
        // Scatter Plot Chart
        if (this.isScatter()) {

          const sanitizedYSeries = metricDataSeries.institutionSeries.filter((item): item is number => item !== null);

          const combinedData = sanitizedRateSeries.map((x, index) => {
            const y = sanitizedYSeries[index];
            const date = metricDataSeries.dates[index];
            return [x, y, date];
          });

          const regressionLineData = [
            [Math.min(...sanitizedRateSeries), linearRegression.slope * Math.min(...sanitizedRateSeries) + linearRegression.intercept],
            [Math.max(...sanitizedRateSeries), linearRegression.slope * Math.max(...sanitizedRateSeries) + linearRegression.intercept],
          ];

          this.updateOptions = {
            grid: {
              left: '40px',
              right: '20px',
              top: '20px',
              bottom: '40px'
            },
            tooltip: {
              trigger: 'item',
              formatter: (params: any) => {
                const [x, y, date] = params.data;
                return `Market Rate: ${x}%<br>Bank Cost: ${y.toFixed(2)}%<br>Date: ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
              }
            },
            xAxis: {
              type: 'value',
              name: 'Market Rate',
              nameLocation: 'middle',
              nameGap: 30
            },
            yAxis: [{
              type: 'value',
              name: 'Bank Cost',
              nameLocation: 'middle',
              nameGap: 27,
            }]
            ,
            series: [
              {
                name: 'Market Rate & Cost',
                data: combinedData,
                color: "#243b5b",
                type: 'scatter'
              },
              {
                name: 'Trend Line',
                type: 'line',
                data: regressionLineData,
                color: this.colors[1],
                symbol: 'line',
                lineStyle: {
                  color: this.colors[1],
                  width: 2
                },

              }
            ],
            graphic: [
              {
                type: 'text',
                left: 100, // Fixed x position
                top: 'top',  // Fixed y position
                style: {
                  text: this.regressionFormula().replace('+ -', '-'),
                  fontSize: 16,
                  fontWeight: 'bold',
                  fill: this.colors[1] // Text color
                }
              }
            ]
          };
        }

        // Bar Line Chart
        else {
          const regressionLineData = metricDataSeries.dates.map((date, index) => {
            const rate = sanitizedRateSeries[index];
            const cost = metricDataSeries.institutionSeries[index];

            // Only calculate y if both rate and cost are defined
            if (rate !== undefined && cost !== undefined) {
              const y = linearRegression.slope * rate + linearRegression.intercept;
              return [date, y]; // Pair each date with the corresponding y-value
            } else {
              return [date, undefined]; // Use undefined for missing data
            }
          });

          const regressionLineDataFormatted = regressionLineData.map(item => {
            // Convert to Date if not already a Date object
            const date = item[0] ? new Date(item[0]) : null;
            // Format date to "MMM yyyy" (e.g., "Mar 2019")
            const formattedDate = date ? `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}` : null;
            return [
              formattedDate,
              item[1]
            ];
          });

          this.updateOptions = {
            xAxis: {
              data: metricDataSeries.dateSeries,
              type: 'category'
            },
            series: [
              {
                name: "Market Rate",
                data: sanitizedRateSeries,
                color: GlobalConstants.rateColor,
                type: 'bar'
              },
              {
                name: "Predicted",
                data: regressionLineDataFormatted,
                color: this.colors[1],
                symbol: 'line',
                lineStyle: {
                  type: 'dashed'
                }
              },
              {
                name: "Actual",
                data: replaceEmptyBankCost,
                color: this.colors[0],
                symbol: 'line',

              }
            ],
            graphic: [
              {
                type: 'text',
                left: 100,  // Fixed x position
                top: 'top', // Fixed y position
                style: {
                  text: this.regressionFormula().replace('+ -', '-'),
                  fontSize: 16,
                  fontWeight: 'bold',
                  fill: this.colors[1]
                }
              }
            ]
          };
        }
      }
      this.echartsInstance?.hideLoading();
    });
  }

  hasOnlyOneDistinctValue<T>(list: T[], property: keyof T): boolean {
    const uniqueValues = new Set(list.map((item) => item[property]));
    return uniqueValues.size === 1;
  }

  subtractQuarters(date: Date | undefined, quarters: number): Date {
    const newDate = new Date(date ?? new Date());
    newDate.setMonth(newDate.getMonth() - quarters * 3);
    return newDate;
  }

  private calculateLinearRegression(x: number[], y: number[]): { slope: number; intercept: number } {
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { slope, intercept };
  }

  clearMetricSearch() {
    this.metricSearch = '';
    this.openMetricCategories = [];
  }

  clearRateSearch() {
    this.rateSearch = '';
    this.openRateCategories = [];
  }

  isMetricCategoryOpen(category: string) {
    return this.openMetricCategories.find(c => c == category);
  }

  isRateCategoryOpen(category: string) {
    return this.openRateCategories.find(c => c == category);
  }

  toggleMetricCategory(category: string) {
    if (this.openMetricCategories.find(c => c == category)) {
      this.openMetricCategories = this.openMetricCategories.filter(c => c != category);
    }
    else {
      this.openMetricCategories.push(category);
    }
  }

  toggleRateCategory(category: string) {
    if (this.openRateCategories.find(c => c == category)) {
      this.openRateCategories = this.openRateCategories.filter(c => c != category);
    }
    else {
      this.openRateCategories.push(category);
    }
  }

  selectMetric(metricId: string): void {
    const metric = this.regressionMetrics().find(m => m.metricId == metricId);
    if (metric) {
      this.sessionService.setAndWait(`regression-metricId-${this.institution()?.institutionType}`, metricId).subscribe(() => {
        this.metric.set(metric);
        this.selectRate(undefined);
      });
      this.metricListOpen = false;
    }

  }

  selectRate(rateId: string | undefined): void {
    let rate: RegressionRate | undefined = undefined;
    const key = `regression-rateId-${this.institution()?.institutionType.toString()}`;

    if (rateId) {
      rate = this.correlationRates().find(r => r.rateIdStatistical == rateId);
    }

    if (rate == undefined) {
      rateId = (this.sessionService.get(key) as RegressionRate)?.rateIdStatistical;
      rate = this.correlationRates().find(r => r.rateIdStatistical == rateId);
    }

    if (rate == undefined) {
      rate = this.correlationRates()[0];
    }

    if (rate) {
      this.rate.set(rate);
      this.rateService.setSelecteRegressionRate(rate);
      this.sessionService.set(key, rate, true);
      this.rateListOpen = false;
    }
  }

  // Did this to add an space between the minus and the number
  formatSlope(value: number): string {
    const formatted = Math.abs(value).toFixed(2);
    return value < 0 ? `- ${formatted}` : formatted;
  }
}
